import { DataTableCard } from "../components/DataTableCard";
import { KpiRail } from "../components/KpiRail";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

type SourcesPageData = Awaited<ReturnType<typeof api.getSources>>;

function QuietAppendixShell({
  title,
  body,
  note
}: {
  title: string;
  body: string;
  note: string;
}) {
  return (
    <section
      style={{
        borderRadius: "24px",
        padding: "24px",
        background: "linear-gradient(180deg, rgba(10, 15, 27, 0.82), rgba(7, 11, 21, 0.74))",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 18px 40px rgba(3, 8, 20, 0.18)"
      }}
    >
      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
          alignItems: "start"
        }}
      >
        <div className="stack-sm">
          <span className="card-eyebrow">Appendix / Trust Layer</span>
          <h2
            style={{
              margin: 0,
              fontSize: "clamp(1.8rem, 3vw, 2.6rem)",
              lineHeight: 1,
              letterSpacing: "-0.04em",
              color: "#f4f8ff"
            }}
          >
            {title}
          </h2>
          <p style={{ margin: 0, color: "rgba(223, 232, 246, 0.78)", lineHeight: 1.6, maxWidth: "60ch" }}>{body}</p>
        </div>
        <div
          style={{
            borderRadius: "18px",
            padding: "16px",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)"
          }}
        >
          <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
            How to read this page
          </div>
          <div style={{ marginTop: "10px", color: "rgba(232, 239, 250, 0.86)", lineHeight: 1.55 }}>{note}</div>
        </div>
      </div>
    </section>
  );
}

function CompactList({
  eyebrow,
  title,
  items,
  tone
}: {
  eyebrow: string;
  title: string;
  items: string[];
  tone: "neutral" | "warning";
}) {
  const accent = tone === "warning" ? "#ffcf7d" : "#7fd0ff";

  return (
    <section
      style={{
        borderRadius: "22px",
        padding: "20px",
        background: "linear-gradient(180deg, rgba(9, 14, 26, 0.88), rgba(7, 11, 21, 0.78))",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 16px 34px rgba(3, 8, 20, 0.18)"
      }}
    >
      <div className="stack-sm">
        <div className="stack-xs">
          <span className="card-eyebrow">{eyebrow}</span>
          <h3 className="card-title">{title}</h3>
        </div>
        <div style={{ display: "grid", gap: "12px" }}>
          {items.map((item, index) => (
            <div
              key={`${item}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: "26px minmax(0, 1fr)",
                gap: "10px",
                alignItems: "start",
                paddingTop: index === 0 ? 0 : "12px",
                borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.06)"
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "26px",
                  height: "26px",
                  borderRadius: "999px",
                  background: `${accent}22`,
                  border: `1px solid ${accent}55`,
                  color: accent,
                  fontSize: "0.76rem",
                  fontWeight: 700
                }}
              >
                {index + 1}
              </span>
              <div style={{ color: "rgba(221, 231, 247, 0.8)", lineHeight: 1.5 }}>{item}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SourcesContent({ data }: { data: SourcesPageData }) {
  const evidenceRows = [
    ["Public trust role", "Curated public trust layer", "Explains evidence basis without exposing raw internal workflow"],
    ["Evidence basis", data.summary, "High-level public evidence posture only"],
    [
      "Coverage footprint",
      `${data.sectionCoverage.length} covered areas`,
      "Supports published surfaces without acting as a data room"
    ],
    [
      "Reliability posture",
      `${data.reliabilityBreakdown.length} public reliability buckets`,
      "Explains confidence basis, not claim-by-claim evidence tagging"
    ]
  ];

  const representativeRows = data.representativeSources.map((item) => [
    item.name,
    `${item.type} | ${item.reliability}`,
    item.reference,
    item.sectionsSupported.join(", ")
  ]);

  const methodologyRows = [
    [
      "Methodology scope",
      "Curated published-basis summary",
      "Explains what supports the live site, not every internal research branch"
    ],
    [
      "Verified vs modeled",
      "Framework disclosed, not fully claim-tagged",
      "Runtime does not yet expose a full claim-level evidence ledger"
    ],
    [
      "Currentness",
      "Controlled but not fully contracted in runtime",
      "No full public source-by-source freshness ledger is exposed here"
    ],
    [
      "Public disclosures",
      `${data.limitations.length} disclosed limitations`,
      "Limits are shown directly instead of being implied away"
    ]
  ];

  const categoryRows = data.sourceCategoryCounts.map((item) => [
    item.category,
    `${item.count}`,
    data.reliabilityBreakdown[0]?.label ?? "Public summary coverage"
  ]);

  return (
    <div className="stack-xl">
      <QuietAppendixShell
        title="Trust contract for the published site."
        body="This page is the appendix-grade basis for the live investor site. It shows what supports the published case, what the methodology summary is actually doing, what remains uncontracted in runtime precision, and what stays intentionally outside the public layer."
        note="Use this page to calibrate trust, not to replace the investment pages. Scan the evidence basis, methodology limits, and exclusions, then move back into the core decision surfaces."
      />

      <KpiRail
        items={[
          {
            label: "Representative sources",
            value: `${data.representativeSources.length}`,
            note: "Named public examples, not full internal inventory",
            tone: "neutral"
          },
          {
            label: "Source categories",
            value: `${data.sourceCategoryCounts.length}`,
            note: "Current evidence groupings visible in public",
            tone: "neutral"
          },
          {
            label: "Coverage areas",
            value: `${data.sectionCoverage.length}`,
            note: "Published diligence areas currently supported",
            tone: "neutral"
          },
          {
            label: "Disclosed limits",
            value: `${data.limitations.length}`,
            note: "Known public trust-layer boundaries",
            tone: "warning"
          }
        ]}
      />

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <DataTableCard
          eyebrow="Evidence Basis"
          title="Trust-layer role"
          description="This appendix exists to define the public evidence posture for the live site, not to act as a primary investment argument."
          columns={["Trust layer", "Current read", "Meaning"]}
          rows={evidenceRows}
        />

        <DataTableCard
          eyebrow="Methodology Scope"
          title="What the runtime does and does not expose"
          description="The public methodology layer is explicit about scope and current gaps instead of pretending runtime precision that does not exist."
          columns={["Area", "Current read", "Implication"]}
          rows={methodologyRows}
        />
      </div>

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <DataTableCard
          eyebrow="Source Basis"
          title="Representative sources"
          description="Examples are shown to explain basis and coverage. They are not a raw source-log dump."
          columns={["Source", "Type / reliability", "Reference", "Supported areas"]}
          rows={representativeRows}
        />

        <DataTableCard
          eyebrow="Coverage Summary"
          title="Source category view"
          description="Category counts keep the evidence layer readable without inflating it into a full inventory browser."
          columns={["Category", "Count", "Current read"]}
          rows={categoryRows}
        />
      </div>

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))"
        }}
      >
        <CompactList
          eyebrow="Current Limitations"
          title="What the public layer is not claiming"
          items={data.limitations}
          tone="warning"
        />
        <CompactList
          eyebrow="Internal-Only Exclusions"
          title="What stays out of public view"
          items={[
            "Raw source-log batches remain internal and are not reproduced here.",
            "Internal process notes and batch workflow detail remain excluded from the public site.",
            "The full internal evidence inventory is not exposed as a browsing interface.",
            "Where runtime lacks explicit claim-level tagging or a full freshness ledger, the gap is disclosed instead of backfilled with invented precision."
          ]}
          tone="neutral"
        />
      </div>
    </div>
  );
}

export function SourcesPage() {
  const { data, loading, error } = useContent("sources-methodology-page", api.getSources);

  return (
    <PageContainer
      eyebrow="Sources & Methodology"
      title="Appendix-grade trust layer."
      lede="Evidence basis, methodology scope, current limitations, and internal-only exclusions are kept visible here in a compact supporting appendix."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading sources and methodology...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? <SourcesContent data={data} /> : null}
    </PageContainer>
  );
}
